import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { userGroupValidators } from "@shared/validators/admin/user-groups-validators";
import { useUserGroupWithConnectionsQuery } from "@/api/queries/admin/UserGroupQueries";
import { useAssignAgentConnectionsMutation, useRemoveAgentConnectionsMutation } from "@/api/mutations/admin/UserGroupMutations";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";

type UserGroup = z.infer<typeof userGroupValidators.userGroupSchema>;

interface ManageConnectionsDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    userGroup?: UserGroup | null;
}

const ManageConnectionsDialog = ({ open, onOpenChange, userGroup }: ManageConnectionsDialogProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedConnections, setSelectedConnections] = useState<Set<string>>(new Set());

    // Fetch all available agent connections
    const { data: allConnectionsData, isLoading: loadingAllConnections } = trpc.agentConnections.find.useQuery(
        { page: 1, per_page: 100 },
        { enabled: open }
    );

    // Fetch current connections for this group
    const { data: groupData, isLoading: loadingGroupData } = useUserGroupWithConnectionsQuery(
        userGroup?.id || "",
        open && !!userGroup?.id
    );

    const assignMutation = useAssignAgentConnectionsMutation();
    const removeMutation = useRemoveAgentConnectionsMutation();

    // Initialize selected connections when data loads
    useEffect(() => {
        if (groupData?.agentConnections) {
            setSelectedConnections(new Set(groupData.agentConnections.map((conn: any) => conn.id)));
        }
    }, [groupData]);

    const allConnections = allConnectionsData?.data || [];
    const filteredConnections = allConnections.filter(
        (conn) =>
            conn.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            conn.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            conn.type.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleToggleConnection = (connectionId: string) => {
        const newSelected = new Set(selectedConnections);
        if (newSelected.has(connectionId)) {
            newSelected.delete(connectionId);
        } else {
            newSelected.add(connectionId);
        }
        setSelectedConnections(newSelected);
    };

    const handleSave = async () => {
        if (!userGroup) return;

        const currentIds = new Set(groupData?.agentConnections?.map((conn: any) => conn.id) || []);
        const newSelectedIds = Array.from(selectedConnections);

        // Find connections to add and remove
        const toAdd = newSelectedIds.filter((id) => !currentIds.has(id));
        const toRemove = Array.from(currentIds).filter((id) => !selectedConnections.has(id));

        try {
            if (toAdd.length > 0) {
                await assignMutation.mutateAsync({
                    userGroupId: userGroup.id,
                    agentConnectionIds: toAdd,
                });
            }

            if (toRemove.length > 0) {
                await removeMutation.mutateAsync({
                    userGroupId: userGroup.id,
                    agentConnectionIds: toRemove,
                });
            }

            onOpenChange(false);
        } catch (error) {
            console.error("Error updating connections:", error);
        }
    };

    const isSaving = assignMutation.isPending || removeMutation.isPending;
    const isLoading = loadingAllConnections || loadingGroupData;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Manage Agent Connections</DialogTitle>
                    <DialogDescription>
                        Select which agent connections members of "{userGroup?.name}" can access.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search connections..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {/* Connection List */}
                    <ScrollArea className="h-[400px] rounded-md border p-4">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin" />
                            </div>
                        ) : filteredConnections.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground">
                                {searchQuery ? "No connections found matching your search." : "No agent connections available."}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredConnections.map((connection) => (
                                    <div
                                        key={connection.id}
                                        className="flex items-start space-x-3 rounded-lg border p-3 hover:bg-accent"
                                    >
                                        <Checkbox
                                            checked={selectedConnections.has(connection.id)}
                                            onCheckedChange={() => handleToggleConnection(connection.id)}
                                            className="mt-1"
                                        />
                                        <div className="flex-1 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium leading-none">{connection.name}</p>
                                                <Badge variant={connection.enabled ? "default" : "secondary"} className="text-xs">
                                                    {connection.type}
                                                </Badge>
                                                {!connection.enabled && (
                                                    <Badge variant="outline" className="text-xs">
                                                        Disabled
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-muted-foreground">{connection.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>

                    {/* Selected count */}
                    <div className="text-sm text-muted-foreground">
                        {selectedConnections.size} connection{selectedConnections.size !== 1 ? "s" : ""} selected
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={isSaving}>
                            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Changes
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ManageConnectionsDialog;
